<template>
    <div>

        <Head :title="__(title)" />
        <div class="flex flex-wrap">
            <div class="max-w-full lg:w-3/5">
                <form @submit.prevent="update" class="bg-white rounded-md shadow overflow-hidden mr-2">
                    <!-- Closed ticket banner -->
                    <div v-if="ticket.closed"
                        class="bg-red-100 text-red-800 p-3 px-8 border-b border-red-200 flex items-center">
                        <icon name="lock-closed" class="w-5 h-5 mr-2" />
                        <span>
                            {{ __('This ticket was closed') }}
                            <span v-if="ticket.closed_at">
                                {{ __('on') }} {{ moment(ticket.closed_at).format('LLL') }}
                            </span>
                            <span v-if="ticket.closed_by">
                                {{ __(' by') }} {{ ticket.closed_by?.name || __('System') }}
                            </span>
                        </span>
                    </div>



                    <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
                       <!-- Customer field (readonly for all users) -->
                        <div class="pr-6 pb-8 w-full lg:w-1/3 flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Customer') }}</div>
                            <div class="font-light text-sm">
                                <span v-if="ticket.user === 'Anonymous'">{{ __('Anonymous') }}</span>
                                <span v-else>{{ ticket.user }}</span>
                            </div>
                        </div>

                        <!-- Priority field -->
                        <select-edit-input placeholder="Search priority" :items="priorities" v-model="form.priority_id"
                            :error="form.errors.priority_id" class="pr-6 pb-8 w-full lg:w-1/3" :label="__('Priority')"
                            :value="ticket.priority" :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Assigned to field -->
                        <select-edit-input
                            v-if="auth.user.role.slug !== 'customer' && !(hidden_fields && hidden_fields.includes('assigned_to'))"
                            placeholder="Search user" :on-input="doFilterUsersExceptCustomer"
                            :items="usersExceptCustomers" v-model="form.assigned_to" :error="form.errors.assigned_to"
                            class="pr-6 pb-8 w-full lg:w-1/3" :label="__('Assigned to')"
                            :value="ticket.assigned_user ?? 'Not Assigned'"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Status field -->
                        <select-edit-input placeholder="Select status to change" :items="availableStatuses"
                            v-model="form.status_id" :error="form.errors.status_id" class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Status')" :value="ticket.status ? ticket.status.name : 'N/A'"
                            :editable="auth.user.role.slug !== 'customer' && user_access.ticket.update && !ticket.closed">
                            <template #selected-option>
                                <div class="flex items-center">
                                    <span class="inline-block w-3 h-3 rounded-full mr-2" :class="{
                                        'bg-green-500': ticket.status?.slug.includes('open') || ticket.status?.slug.includes('active'),
                                        'bg-yellow-500': ticket.status?.slug.includes('pending') || ticket.status?.slug.includes('hold'),
                                        'bg-red-500': ticket.status?.slug.includes('closed') || ticket.status?.slug.includes('rejected'),
                                        'bg-blue-500': ticket.status?.slug.includes('new') || ticket.status?.slug.includes('processing'),
                                        'bg-gray-500': !ticket.status || !['open', 'active', 'pending', 'hold', 'closed', 'rejected', 'new', 'processing'].some(s => ticket.status.slug.includes(s))
                                    }"></span>
                                    {{ ticket.status ? ticket.status.name : 'N/A' }}
                                </div>
                            </template>
                            <template #option="{ item }">
                                <div class="flex items-center">
                                    <span class="inline-block w-3 h-3 rounded-full mr-2" :class="{
                                        'bg-green-500': item.slug.includes('open') || item.slug.includes('active'),
                                        'bg-yellow-500': item.slug.includes('pending') || item.slug.includes('hold'),
                                        'bg-red-500': item.slug.includes('closed') || item.slug.includes('rejected'),
                                        'bg-blue-500': item.slug.includes('new') || item.slug.includes('processing'),
                                        'bg-gray-500': !['open', 'active', 'pending', 'hold', 'closed', 'rejected', 'new', 'processing'].some(s => item.slug.includes(s))
                                    }">
                                    </span>
                                    {{ item.name }}
                                </div>
                            </template>
                        </select-edit-input>

                        <!-- Ticket type field -->
                        <select-edit-input v-if="!(hidden_fields && hidden_fields.includes('ticket_type'))"
                            placeholder="Search type" :items="types" v-model="form.type_id" :error="form.errors.type_id"
                            class="pr-6 pb-8 w-full lg:w-1/3" :label="__('Ticket type')" :value="ticket.type"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Department field -->
                        <select-edit-input v-if="!(hidden_fields && hidden_fields.includes('department'))"
                            @change="getCategories()" placeholder="Search department" :items="departments"
                            v-model="form.department_id" :error="form.errors.department_id"
                            class="pr-6 pb-8 w-full lg:w-1/3" :label="__('Department')" :value="ticket.department"
                            :editable="user_access.ticket.update && !ticket.closed">
                        </select-edit-input>

                        <!-- Category field -->
                        <select-edit-input ref="category"
                            v-if="!(hidden_fields && hidden_fields.includes('category')) && form.department_id"
                            @change="getSubCategories" placeholder="Search category" :items="categories"
                            v-model="form.category_id" :error="form.errors.category_id"
                            class="pr-6 pb-8 w-full lg:w-1/3" :label="__('Category')"
                            :editable="user_access.ticket.update && !ticket.closed" />

                        <!-- Sub-category field -->
                        <select-edit-input ref="sub_category"
                            v-if="!(hidden_fields && hidden_fields.includes('category')) && form.category_id"
                            placeholder="Search sub-category" :items="sub_categories" v-model="form.sub_category_id"
                            :error="form.errors.sub_category_id" class="pr-6 pb-8 w-full lg:w-1/3"
                            :label="__('Sub Category')" :editable="user_access.ticket.update && !ticket.closed" />

                        <!-- Created date (readonly) -->
                        <div class="assigned_user pr-6 pb-8 w-full lg:w-1/3 flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Created') }} </div>
                            <div class="font-light text-sm">{{ moment(ticket.created_at).fromNow() }}</div>
                        </div>

                        <!-- Channel used (readonly) -->
                        <div class="assigned_user pr-6 pb-8 w-full lg:w-1/3 flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Channel Used') }} </div>
                            <div class="font-light text-sm">{{ ticket.source }}</div>
                        </div>

                        <!-- Subject field (readonly) -->
                        <div class="pr-6 pb-8 w-full lg:w-2/3 flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Subject') }}</div>
                            <div class="font-light text-sm">{{ ticket.subject }}</div>
                        </div>

                        <!-- Request Details (readonly) -->
                        <div class="assigned_user pr-6 pb-8 w-full lg:w-full flex flex-col">
                            <div class="font-bold text-sm mb-1">{{ __('Request Details') }}</div>
                            <div class="font-light text-sm" v-html="ticket.details"></div>
                        </div>

                        <!-- File Attachments (readonly) -->
                        <!-- <div class="pr-6 pb-8 w-full lg:w-full flex-col">
                            <div v-if="attachments.length" class="flex items-center justify-between pr-6 pt-8 w-full"
                                v-for="(file, fi) in attachments" :key="fi">
                                <div class="flex-1 pr-1">
                                    {{ file.name }} <span class="text-gray-500 text-xs">({{ getFileSize(file.size) }})</span>
                                    <a v-if="file.user" class="text-sm"
                                        :href="this.route('users.edit', file.user.id)">{{ file.user.first_name }} {{ file.user.last_name }}</a>
                                    at <span class="text-sm">{{ file.created_at }}</span>
                                </div>
                                <div class="a__buttons flex justify-end items-center">
                                    <button type="button" class="btn flex items-center"
                                        @click="downloadAttachment(file)">
                                        {{ __('Download') }}</button>
                                </div>
                            </div>
                        </div> -->

                        <!-- File Attachments section -->
                        <div class="pr-6 pb-8 w-full lg:w-full flex-col">
                            <div v-if="attachments.length" class="mb-4">
                                <h3 class="font-bold text-sm mb-2">{{ __('Attachments') }}</h3>
                                <div v-for="(file, fi) in attachments" :key="fi"
                                    class="flex items-center justify-between py-2 border-b">
                                    <div class="flex-1 pr-1">
                                        <div class="flex items-center">
                                            <icon name="paper-clip" class="w-4 h-4 mr-2 text-gray-500" />
                                            <span>
                                                {{ file.name }}
                                                <span class="text-gray-500 text-xs">({{ getFileSize(file.size)
                                                    }})</span>
                                            </span>
                                        </div>
                                        <div v-if="file.user" class="text-xs text-gray-500 mt-1">
                                            Uploaded by {{ file.user.first_name }} {{ file.user.last_name }} at {{
                                            file.created_at }}
                                        </div>
                                    </div>
                                    <div class="flex justify-end items-center">
                                        <button type="button" class="btn flex items-center text-sm"
                                            @click="downloadAttachment(file)">
                                            <icon name="download" class="w-4 h-4 mr-1" />
                                            {{ __('Download') }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Rating section (only for customers on closed tickets) -->
                        <div class="flex flex-col w-full"
                            v-if="ticket.status && ticket.closed && !ticket.review && auth.user.role.slug === 'customer'">
                            <div class="assigned_user star__review pb-5 w-full lg:w-1/3 flex flex-col">
                                <div class="font-bold">{{ __('How do you rate this support service?') }} </div>
                                <div class="star-rating pt-5">
                                    <input type="radio" id="5-stars" name="rating" v-model="form.rating" value="5" />
                                    <label for="5-stars" class="star">&#9733;</label>
                                    <input type="radio" id="4-stars" name="rating" v-model="form.rating" value="4" />
                                    <label for="4-stars" class="star">&#9733;</label>
                                    <input type="radio" id="3-stars" name="rating" v-model="form.rating" value="3" />
                                    <label for="3-stars" class="star">&#9733;</label>
                                    <input type="radio" id="2-stars" name="rating" v-model="form.rating" value="2" />
                                    <label for="2-stars" class="star">&#9733;</label>
                                    <input type="radio" id="1-star" name="rating" v-model="form.rating" value="1" />
                                    <label for="1-star" class="star">&#9733;</label>
                                </div>
                            </div>
                            <textarea-input v-model="form.review" :error="form.errors.review"
                                class="pr-6 pb-4 w-full lg:w-1/3" :label="__('Feedback')" />
                            <div class="flex lg:w-1/4 mb-4"><button class="btn-indigo" type="submit">{{ __('Submit')
                                    }}</button></div>
                        </div>
                    </div>

                     <div class="ticket-action-bar px-8 py-4 bg-gray-50 border-t border-gray-100 flex flex-wrap items-center gap-3">
                <!-- Delete button - only for admin -->
                <button v-if="user_access.ticket.delete && auth.user.role.slug === 'admin'"
                        class="ticket-action-btn ticket-action-btn-danger" tabindex="-1" type="button" @click="destroy">
                    {{ __('Delete') }}
                </button>

                <!-- Send Email button - only for admin and manager -->
                <Link v-if="['admin', 'manager'].includes(auth.user.role.slug)"
                      :href="route('tickets.email.create', ticket.id)" class="ticket-action-btn ticket-action-btn-primary">
                    {{ __('Send Email') }}
                </Link>

                <!-- Close Ticket button - only for admin and manager -->
                <button v-if="!ticket.closed && user_access.ticket.update && ['admin', 'manager'].includes(auth.user.role.slug)"
                        class="ticket-action-btn ticket-action-btn-danger" type="button" @click="openCloseTicketModal">
                    <icon name="lock-closed" class="w-4 h-4 mr-1 inline" />
                    {{ __('Close Ticket') }}
                </button>

                <!-- Reopen Ticket button - only for admin and manager -->
                <button v-if="ticket.closed && user_access.ticket.update && ['admin', 'manager'].includes(auth.user.role.slug)"
                        class="ticket-action-btn ticket-action-btn-success" @click="openReopenModal">
                    <icon name="lock-open" class="w-4 h-4 mr-1 inline" />
                    {{ __('Reopen Ticket') }}
                </button>

                <!-- Audit Trail - only for admin -->
                <Link v-if="auth.user.role.slug === 'admin'"
                      :href="route('tickets.audit', ticket.id)" class="ticket-action-btn ticket-action-btn-neutral">
                    {{ __('View Audit Trail') }}
                </Link>
                        <loading-button :loading="form.processing" class="ticket-action-btn ticket-action-btn-primary ticket-action-save ml-auto" type="submit">
                            {{ __('Save') }}
                        </loading-button>
                    </div>
                </form>
            </div>

            <!-- Chat area remains unchanged -->
            <div class="max-w-full lg:w-2/5">
                <div class="bg-white rounded-md shadow overflow-hidden ml-2 chat-area comment-box flex-1 flex flex-col">
                    <div class="flex-3">
                        <div class="chat-header flex flex-col pb-3">
                            <h3 class="text-xl">{{ __('Ticket discussion') }}</h3>
                            <p class="text-sm font-light">{{ __('Comment histories for this ticket will be available here.') }}</p>
                        </div>
                    </div>
                    <div class="messages flex-1 overflow-auto reverse__order">
                        <div v-for="(comment, index) in comments.slice().reverse()" :key="index"
                            class="message mb-4 flex">
                            <div v-if="comment.user_id !== user.id" class="flex-2">
                                <div class="w-12 h-12 relative">
                                    <span class="w-12 h-12 rounded-full mx-auto user_icon" alt="chat-user"
                                        v-if="comment.user">
                                        {{ Array.from(comment.user.first_name)[0] }}</span>
                                </div>
                            </div>
                            <div v-if="comment.user_id !== user.id" class="flex-1 px-2 mt__wrap">
                                <h3 class="font-bold pb-2 text-sm pt-1" v-if="comment.user">{{ comment.user.first_name
                                }} {{
                                        comment.user.last_name }}</h3>
                                <div
                                    class="inline-block bg-gray-300 user-comment-round p-2 px-3 text-gray-700 leading-5">
                                    <span>{{ stripHtml(comment.details) }}</span>
                                </div>
                                <div class="pl-4"><small class="text-gray-500">{{
                                    moment(comment.updated_at).fromNow(true)
                                        }}</small></div>
                            </div>

                           <div v-if="comment.user_id === user.id" class="flex-1 mt__wrap px-2 text-right">
                                <div class="inline-block bg-blue rounded p-2 px-3 text-white leading-5"
                                    v-html="cleanCommentHtml(comment.details)">
                                </div>
                                <div class="pr-4"><small class="text-gray-500">{{
                                    moment(comment.updated_at).fromNow(true)
                                }}</small></div>
                            </div>
                        </div>
                    </div>
                    <div class="flex-2 pt-4 pb-3">
                        <div class="write bg-white shadow flex rounded-lg">
                            <div class="flex-1 ticket_comment_edit">
                                <ckeditor id="ticketDiscussion" :editor="editor" v-model="comment" name="comment"
                                    :config="editorConfig" autofocus></ckeditor>
                            </div>
                            <div class="flex-2 w-35 p-2 flex content-center items-center">
                                <div class="flex-1">
                                    <button class="bg-blue w-10 h-10 rounded-full flex justify-center items-center"
                                        @click="submitComment">
                                        <icon class="w-4 h-4 fill-gray-100" name="send" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Reopen Ticket Modal -->
        <Modal :show="showReopenModal" @close="closeReopenModal" max-width="md">
            <div class="p-6">
                <h2 class="text-lg font-medium text-gray-900 mb-4">
                    {{ __('Reopen Ticket') }} #{{ ticket.uid }}
                </h2>

                <select-input v-model="reopenForm.status_id" :error="reopenForm.errors.status_id" class="pb-4 w-full"
                    :label="__('New Status *')" required>
                    <option v-for="status in reopenStatuses" :key="status.id" :value="status.id">
                        {{ status.name }}
                    </option>
                </select-input>

                <textarea-input v-model="reopenForm.reason" :error="reopenForm.errors.reason" class="pb-4 w-full"
                    :label="__('Reason for Reopening *')" required rows="5" />

                <div class="flex justify-end mt-6 space-x-2">
                    <button type="button" @click="closeReopenModal" class="btn-gray">
                        {{ __('Cancel') }}
                    </button>
                    <LoadingButton @click="submitReopen" :loading="reopenForm.processing" class="btn-green">
                        {{ reopenForm.processing ? __('Reopening...') : __('Reopen Ticket') }}
                    </LoadingButton>
                </div>
            </div>
        </Modal>

        <!-- Close Ticket Modal -->
        <Modal :show="showCloseTicketModal" @close="closeCloseTicketModal" max-width="md">
            <div class="p-6">
                <h2 class="text-lg font-medium text-gray-900 mb-4">
                    {{ __('Close Ticket') }}
                </h2>

                <form @submit.prevent="closeTicket"> <!-- Add form wrapper with prevent modifier -->
                    <textarea-input v-model="closeForm.resolution" :error="closeForm.errors.resolution"
                        class="pb-4 w-full" :label="__('Resolution Details *')" required rows="5" />

                    <div class="flex justify-end mt-6 space-x-2">
                        <button type="button" @click="closeCloseTicketModal" class="modal-action-btn modal-action-btn-secondary">
                            {{ __('Cancel') }}
                        </button>
                        <LoadingButton type="button" @click="closeTicket" :loading="closeForm.processing"
                            class="modal-action-btn modal-action-btn-danger">
                            {{ closeForm.processing ? __('Closing...') : __('Close Ticket') }}
                        </LoadingButton>
                    </div>
                </form>
            </div>
        </Modal>
    </div>
</template>

<script>
import { Link, Head } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Modal from '@/Shared/Modal'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import moment from 'moment'
import TextEditInput from '@/Shared/TextEditInput'
import SelectInput from '@/Shared/SelectInput'
import TextareaInput from '@/Shared/TextareaInput'
import LoadingButton from '@/Shared/LoadingButton'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import SelectEditInput from '@/Shared/SelectEditInput'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import UploadAdapter from '@/Shared/UploadAdapter';
import axios from 'axios'
import CKEditor from '@ckeditor/ckeditor5-vue';

export default {
    components: {
        LoadingButton,
        SelectInput,
        TextInput,
        TextEditInput,
        TextareaInput,
        Link,
        Head,
        Icon,
        SelectInputFilter,
        SelectEditInput,
        ckeditor: CKEditor.component,
        Modal,
    },
    layout: Layout,
    props: {
        title: String,
        ticket: Object,
        priorities: Array,
        statuses: Array,
        types: Array,
        departments: Array,
        all_categories: { required: false },
        customers: Array,
        usersExceptCustomers: Array,
        attachments: Array,
        comments: Array,
        auth: Object,
        hidden_fields: Object,
    },
    data() {
        return {
            user: this.$page.props.auth.user,
            type_status: [],
            categories: this.all_categories.filter(cat => cat.department_id === this.ticket.department_id),
            sub_categories: this.all_categories.filter(cat => cat.parent_id === this.ticket.category_id),
            comment: '',
            source: '',
            editCustomer: false,
            enableEditor: false,
            user_access: this.$page.props.auth.user.access,
            editor: ClassicEditor,
            editorConfig: {
                toolbar: ['heading', '|', 'bold', 'italic', 'link', '|', 'undo', 'redo', '|', 'bulletedList', 'numberedList', '|', 'outdent', 'indent', '|', 'insertTable', 'blockQuote', '|', 'imageUpload', 'mediaEmbed', '|'],
                table: {
                    toolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
                },
                extraPlugins: [this.uploader],
            },
            showCloseTicketModal: false,
            showReopenModal: false,
            availableStatuses: this.statuses.filter(status => !status.slug.includes('closed')),
            reopenForm: this.$inertia.form({
                status_id: null,
                reason: '',
            }),
            reopenStatuses: this.statuses.filter(status => !status.slug.includes('closed')),
            closeForm: this.$inertia.form({
                resolution: '',
                root_cause_id: null,
                resolution_id: null,
            }),
            form: this.$inertia.form({
                user_id: this.ticket.user_id,
                priority_id: this.ticket.priority_id,
                status_id: this.ticket.status_id,
                department_id: this.ticket.department_id,
                category_id: this.ticket.category_id ?? null,
                sub_category_id: this.ticket.sub_category_id,
                assigned_to: this.ticket.assigned_to,
                type_id: this.ticket.type_id,
                rating: 0,
                review: '',
            }),
        };
    },
    created() {
        if (this.auth.user.role.slug === 'customer' && this.statuses.length) {
            this.type_status = this.statuses.filter(status => (status.id === this.form.status_id) || status.name.match(/Close.*/))
        } else {
            this.type_status = this.statuses
        }
    },
    methods: {
        cleanCommentHtml(html) {
        if (!html) return '';

        // Remove empty paragraphs
        let cleaned = html.replace(/<p>\s*&nbsp;\s*<\/p>/gi, '');
        // Remove trailing &nbsp;
        cleaned = cleaned.replace(/(&nbsp;)+$/gi, '');
        // Trim whitespace
        cleaned = cleaned.trim();

        return cleaned;
    },
        moment(date) {
            return moment(date);
        },
        getCategories() {
            this.ticket.category = 'N/A';
            this.form.category_id = null;
            this.categories = this.all_categories.filter(cat => cat.department_id === this.form.department_id)
        },
        stripHtml(html) {
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            return tmp.textContent || tmp.innerText || "";
        },
        getSubCategories() {
            this.sub_categories = this.all_categories.filter(cat => cat.parent_id === this.form.category_id)
            this.ticket.sub_category = 'N/A';
            this.form.sub_category_id = null;
        },
        uploader(editor) {
            editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
                return new UploadAdapter(loader);
            };
        },
        doFilter(e) {
            axios.get(this.route('filter.customers', { search: e.target.value })).then((res) => {
                this.customers.splice(0, this.customers.length, ...res.data);
            })
        },
        doFilterUsersExceptCustomer(e) {
            axios.get(this.route('filter.users_except_customer', { search: e.target.value })).then((res) => {
                this.usersExceptCustomers.splice(0, this.usersExceptCustomers.length, ...res.data);
            })
        },
        getFileSize(size) {
            const i = Math.floor(Math.log(size) / Math.log(1024))
            return (size / Math.pow(1024, i)).toFixed(2) * 1 + ' ' + ['B', 'KB', 'MB', 'GB', 'TB'][i]
        },
       downloadAttachment(file) {
    const startTime = performance.now();
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const isImage = ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension);

    try {
        console.log('Starting attachment download process', {
            fileId: file.id,
            fileName: file.name,
            fileType: fileExtension,
            fileSize: file.size,
            isImage,
            hasOriginalUrl: !!file.original_url,
            hasPath: !!file.path
        });

        const url = file.original_url ? file.original_url : file.path;
        console.log('Selected download source:', {
            source: file.original_url ? 'original_url' : 'path',
            url: url
        });

        if (url.startsWith('http')) {
            console.log('Processing external URL attachment');

            if (isImage) {
                this.$toast.info('Click the image to view full size', { timeout: 3000 });
            }

            const newWindow = window.open(url, '_blank', 'noopener,noreferrer');

            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                console.warn('Popup window was blocked');

                this.$toast.info(`Please visit this URL to view the file: ${url}`, {
                    timeout: 10000,
                    action: {
                        text: 'Copy URL',
                        onClick: () => {
                            navigator.clipboard.writeText(url)
                                .then(() => this.$toast.success('Copied!'))
                                .catch(e => console.error('Copy failed:', e));
                        }
                    }
                });
            }
        } else {
            console.log('Processing local file attachment');

            const link = document.createElement("a");
            const downloadUrl = url.startsWith('/')
                ? window.location.origin + url
                : window.location.origin + '/files/' + url;

            link.href = downloadUrl;
            link.download = file.name;
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                window.URL.revokeObjectURL(downloadUrl);
            }, 100);
        }

        const duration = performance.now() - startTime;
        console.log(`Download completed in ${duration.toFixed(2)}ms`, {
            fileType: fileExtension,
            method: url.startsWith('http') ? 'external' : 'internal',
            size: file.size
        });

    } catch (error) {
        const duration = performance.now() - startTime;
        console.error('Download failed:', {
            error: {
                name: error.name,
                message: error.message,
                stack: error.stack
            },
            context: {
                fileState: {
                    id: file.id,
                    name: file.name,
                    type: fileExtension,
                    size: file.size
                },
                browser: {
                    userAgent: navigator.userAgent,
                    online: navigator.onLine
                },
                duration
            }
        });

        const errorMessage = error.message.includes('Failed to fetch') ?
            'File unavailable. It may have been moved or deleted.' :
            error.name === 'SecurityError' ?
            'Download blocked by security restrictions.' :
            'Download failed. Please try again.';

        this.$toast.error(errorMessage);
    }
},

        update() {
            this.form.post(this.route('tickets.update', this.ticket.id))
        },
        destroy() {
            if (confirm('Are you sure you want to delete this ticket?')) {
                this.$inertia.delete(this.route('tickets.destroy', this.ticket.id))
            }
        },
        openCloseTicketModal() {
            this.showCloseTicketModal = true;
        },
        closeCloseTicketModal() {
            this.showCloseTicketModal = false;
            this.closeForm.reset();
            this.closeForm.errors = {};
        },
        closeTicket() {
            this.closeForm.post(this.route('tickets.close', this.ticket.id), {
                preserveScroll: true,
                onSuccess: () => {
                    // Check if ticket has a customer with email
                    if (this.ticket.user && this.ticket.user.email) {
                        this.$nextTick(() => {
                            if (confirm('Would you like to send the resolution details to the customer?')) {
                                this.sendResolutionEmail();
                            }
                        });
                    } else {
                        this.$nextTick(() => {
                            this.$toast.info('This ticket was submitted anonymously - no email will be sent');
                        });
                    }
                    this.closeCloseTicketModal();
                    // Use $nextTick to ensure the modal is closed before reloading
                    this.$nextTick(() => {
                        this.$inertia.reload({ only: ['ticket'] });
                    });
                },
                onError: (errors) => {
                    this.$toast.error('Failed to close ticket');
                }
            });
        },
        sendResolutionEmail() {
            if (!this.ticket.user || !this.ticket.user.email) {
                this.$toast.error('Cannot send email - no customer email available');
                return;
            }

            axios.post(this.route('tickets.email.resolution', this.ticket.id), {
                resolution: this.closeForm.resolution
            }).then(response => {
                this.$toast.success('Resolution email sent successfully');
            }).catch(error => {
                this.$toast.error('Failed to send resolution email');
            });
        },
        openReopenModal() {
            const openStatus = this.statuses.find(s => !s.slug.includes('closed'));
            this.reopenForm.status_id = openStatus?.id || null;
            this.showReopenModal = true;
        },
        closeReopenModal() {
            this.showReopenModal = false;
            this.reopenForm.reset();
            this.reopenForm.errors = {};
        },
        submitReopen() {
            this.reopenForm.post(this.route('tickets.reopen', this.ticket.id), {
                preserveScroll: true,
                onSuccess: () => {
                    this.closeReopenModal();
                    this.$toast.success('Ticket reopened successfully');
                    this.$inertia.reload({ only: ['ticket'] });
                },
                onError: (errors) => {
                    this.$toast.error('Failed to reopen ticket');
                    if (errors.reason) {
                        this.$toast.error(errors.reason);
                    }
                }
            });
        },
        submitComment() {
            const messageData = {
                comment: this.comment,
                user_id: this.user.id,
                _token: this.$page.props.csrf_token,
                ticket_id: this.ticket.id
            }
            this.comment = ''
            axios.post(this.route('ticket.comment'), messageData).then((response) => {
                if (response.data) {
                    this.comments.push(response.data)
                }
                this.$emit('comment submitted!')
            }).catch((error) => {
                console.log(error)
            })
        },
    }
};
</script>

<style scoped>
.ticket-action-bar {
    row-gap: 0.75rem;
}

.ticket-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 2.5rem;
    padding: 0.625rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid transparent;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1;
    transition: all 0.2s ease;
}

.ticket-action-btn:hover {
    text-decoration: none;
    transform: translateY(-1px);
}

.ticket-action-btn-danger {
    background: #fff1f2;
    border-color: #fecdd3;
    color: #dc2626;
}

.ticket-action-btn-danger:hover {
    background: #ffe4e6;
}

.ticket-action-btn-primary {
    background: linear-gradient(135deg, #6d5dfc 0%, #5b4de3 100%);
    border-color: #5b4de3;
    color: #ffffff;
    box-shadow: 0 10px 22px -14px rgba(91, 77, 227, 0.9);
}

.ticket-action-btn-primary:hover {
    background: linear-gradient(135deg, #5b4de3 0%, #4c40cb 100%);
    box-shadow: 0 14px 26px -16px rgba(76, 64, 203, 0.95);
}

.ticket-action-btn-success {
    background: #ecfdf3;
    border-color: #bbf7d0;
    color: #15803d;
}

.ticket-action-btn-success:hover {
    background: #dcfce7;
}

.ticket-action-btn-neutral {
    background: #eff6ff;
    border-color: #bfdbfe;
    color: #2563eb;
}

.ticket-action-btn-neutral:hover {
    background: #dbeafe;
}

.ticket-action-save {
    min-width: 5.25rem;
    padding-inline: 1.25rem;
}

.modal-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 2.75rem;
    padding: 0.7rem 1.15rem;
    border-radius: 0.75rem;
    border: 1px solid transparent;
    font-size: 0.95rem;
    font-weight: 600;
    line-height: 1;
    transition: all 0.2s ease;
}

.modal-action-btn:hover {
    transform: translateY(-1px);
}

.modal-action-btn-secondary {
    background: #ffffff;
    border-color: #d1d5db;
    color: #374151;
    box-shadow: 0 6px 16px -14px rgba(17, 24, 39, 0.35);
}

.modal-action-btn-secondary:hover {
    background: #f9fafb;
}

.modal-action-btn-danger {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    border-color: #dc2626;
    color: #ffffff;
    box-shadow: 0 12px 24px -16px rgba(220, 38, 38, 0.95);
}

.modal-action-btn-danger:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    box-shadow: 0 16px 28px -18px rgba(185, 28, 28, 0.95);
}
</style>
